// reportListUtils.js
import { format } from 'date-fns';
import { Report } from '../models/Report';
import { PURGE_THRESHOLD_MONTHS, MAX_REPORTS_TO_KEEP } from './reportListConstants';
import { createLogger } from '../services/Debug';

// Clean report data management
export function normalizeReport(reportData) {
  // If it's already a Report instance, return it
  const log = createLogger('normalizeReport');
  if (reportData instanceof Report) {
    return reportData;
  }
  
  // Create a new Report instance
  return new Report(reportData);
}



// Get report content (handles corrected content)
export function getReportContent(report) {
  return report.isCorrected ? (report.correctedContent || report.content) : report.content;
}

export function purgeOldReports(reports) {
  const thresholdDate = new Date();
  thresholdDate.setMonth(thresholdDate.getMonth() - PURGE_THRESHOLD_MONTHS); 
  
  let filtered = reports.filter(report => {
    const reportDate = new Date(report.createdAt);
    return reportDate >= thresholdDate;
  });
  
  if (filtered.length > MAX_REPORTS_TO_KEEP) {
    filtered.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
    filtered = filtered.slice(0, MAX_REPORTS_TO_KEEP);
  }

  return filtered;
}

// LocalStorage functions
export function saveReportsToLocalStorage(reports) {
  const log = createLogger('saveReportsToLocalStorage');
  try {
    // Directly persist reports (class instances serialize their enumerable fields)
    localStorage.setItem('reports', JSON.stringify(reports));
  } catch (error) {
    log.error('Error saving reports to localStorage:', error);
  }
}

export function loadReportsFromLocalStorage() {
  const log = createLogger('loadReportsFromLocalStorage');
  try {
    const stored = localStorage.getItem('reports');
    if (!stored) return [];
    
    const parsedData = JSON.parse(stored);
    // Convert to Report instances
    return parsedData.map(data => new Report(data));
  } catch (error) {
    log.error('Error loading reports from localStorage:', error);
    return [];
  }
}

export function saveLastRefreshDateToLocalStorage(date) {
  localStorage.setItem('lastRefreshDate', date);
}

export function loadLastRefreshDateFromLocalStorage() {
  const stored = localStorage.getItem('lastRefreshDate');
  return stored ? new Date(stored) : null;
}

// Date functions
export function getLatestReportDate(reports) {
  if (!reports || !reports.length) return null;
  return reports.reduce((latest, r) => {
    const rd = new Date(r.createdAt);
    return rd > latest ? rd : latest;
  }, new Date(reports[0].createdAt));
}

export function getOldestReportDate(reports) {
  if (!reports || !reports.length) return null;
  return reports.reduce((oldest, r) => {
    const rd = new Date(r.createdAt);
    return rd < oldest ? rd : oldest;
  }, new Date(reports[0].createdAt));
}

export function mergeReports(existing, incoming) {
  const map = new Map();
  
  // Add existing reports to map
  existing.forEach(r => {
    map.set(r.id, r);
  });
  
  // Process incoming reports
  incoming.forEach(r => {
    const existingReport = map.get(r.id);
    
    if (!existingReport) {
      // New report
      map.set(r.id, new Report(r));
    } else {
      // Update existing report but preserve local UI state
      const updatedReport = new Report({
        ...r,
        isRead: existingReport.isRead // Preserve read state
      });
      map.set(r.id, updatedReport);
    }
  });
  
  // Sort by date (newest first)
  return Array.from(map.values()).sort((a, b) => 
    new Date(b.createdAt) - new Date(a.createdAt)
  );
}

export function capitalizeFirstLetter(str) {
  if (!str || typeof str !== 'string') return '';
  return str[0].toUpperCase() + str.slice(1).toLowerCase();
}

export function formatDate(date) {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${day}/${month}`;
}

export function groupReportsByDay(reports, dateFilter) {
  const filteredReports = filterReportsByDate(reports, dateFilter);

  return filteredReports.reduce((groups, report) => {
    if (!report || !report.createdAt) return groups;
    const rd = new Date(report.createdAt);
    const dateKey = format(rd, 'dd/MM');
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(report);
    return groups;
  }, {});
}

export function filterReportsByDate(reports, dateFilter) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (dateFilter) {
    case 'today':
      return reports.filter(report => {
        const reportDate = new Date(report.createdAt);
        reportDate.setHours(0, 0, 0, 0);
        return reportDate.getTime() === today.getTime();
      });
    case '2days':
      const twoDaysAgo = new Date(today);
      twoDaysAgo.setDate(today.getDate() - 2);
      return reports.filter(report => {
        const reportDate = new Date(report.createdAt);
        return reportDate >= twoDaysAgo;
      });
    case '3days':
      const threeDaysAgo = new Date(today);
      threeDaysAgo.setDate(today.getDate() - 3);
      return reports.filter(report => {
        const reportDate = new Date(report.createdAt);
        return reportDate >= threeDaysAgo;
      });
    case '1week':
      const oneWeekAgo = new Date(today);
      oneWeekAgo.setDate(today.getDate() - 7);
      return reports.filter(report => {
        const reportDate = new Date(report.createdAt);
        return reportDate >= oneWeekAgo;
      });
    case 'unread':
      return reports.filter(report => !report.isRead);
    case 'all':
      return reports;
    default:
      return reports;
  }
}

export function createMailtoLink(email, subject, body) {
  const encodedBody = body.replace(/\r?\n/g, '%0D%0A');
  return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodedBody}`;
}

// Text manipulation functions
export function removeSpecialChars(text) {
  return text
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?p>/gi, '\n')
    .replace(/[*]/g, '')
    .replace(/[#]/g, '')
    .replace(/[•]/g, '')
    .replace(/[|]/g, '')
    .replace(/[\t ]/g, ' ')
    .replace(/[  ]/g, ' ')
    .replace(/[\u200B-\u200F\u00A0]/g, ' ')
    .replace(/[ ]{2,}/g, ' ')
    .trim();
}

export function doubleLineBreaks(text) {
  return text.replace(/\r?\n/g, '\n\n');
}

export function halveLineBreaks(text) {
  return text.replace(/\n\s*\n/g, '\n');
}



// Extract unique report types from reports array
export function extractUniqueReportTypes(reports) {
  return Array.from(new Set(reports.map((r) => r.reportType || '')));
}

// Helper functions for report status and read tracking
export function isReportFromToday(report) {
  if (!report || !report.createdAt) return false;
  const today = new Date();
  const reportDate = new Date(report.createdAt);
  today.setHours(0, 0, 0, 0);
  reportDate.setHours(0, 0, 0, 0);
  return reportDate.getTime() === today.getTime();
}

export function shouldShowUnreadIndicator(report) {
  return report && !report.isRead;
}

export function isReportProcessing(report) {
  return report && report.status === 'processing';
}

export function isReportClickable(report) {
  // All reports are clickable - processing reports will trigger status check
  return report && report.status;
}

export function isReportStatusCheckable(report) {
  return report && report.status === 'processing';
}

export function canReportBeOpened(report) {
  // Only non-processing reports can be opened for content viewing
  return report && report.status !== 'processing';
}